import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useQuery, useSwal } from '@kiper/hooks';
import { useContext, useEffect, useMemo, useState } from 'react';
import { apolloErrorHandler } from '@kiper/fns';
import Component from './RenewPassword';
import authContext from '../../../services/auth/context';
import { PASSWORD_REGEX } from '../constants/passwordValidation';

export default function RenewPassword(props) {
  const [t, i18n] = useTranslation('renew-password');
  const query = useQuery();
  const { toast } = useSwal();

  const auth = useContext(authContext);

  // Uma vez que o BE pede o código MFA, o campo fica revelado até o fim do
  // fluxo — mesmo que `auth.renewMfaCodeRequired` volte a `false` durante o
  // reenvio (renew.request reseta a flag a cada submit).
  const [mfaCodeRequired, setMfaCodeRequired] = useState(false);

  useEffect(() => {
    if (auth.renewMfaCodeRequired) {
      setMfaCodeRequired(true);
    }
  }, [auth.renewMfaCodeRequired]);

  const validationSchema = useMemo(
    () =>
      yup.object({
        currentPassword: yup.string().required(),
        newPassword: yup
          .string()
          .matches(PASSWORD_REGEX)
          .required(),
        passwordConfirmation: yup
          .string()
          .oneOf([yup.ref('newPassword'), null], 'passwordConfirmation')
          .required(),
        mfaCode: mfaCodeRequired
          ? yup
              .string()
              .matches(/^\d{6}$/, 'mfa-code')
              .required('mfa-code')
          : yup.string(),
      }),
    [mfaCodeRequired],
  );

  const onSubmit = async ({
    username,
    currentPassword,
    newPassword,
    mfaCode,
  }) =>
    auth.renew({
      username,
      currentPassword,
      newPassword,
      ...(mfaCode ? { mfaCode } : {}),
    });

  const formikBag = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      passwordConfirmation: '',
      mfaCode: '',
      username: query.get('email'),
    },

    initialErrors: {
      lowercase: true,
      uppercase: true,
      required: true,
      specialCharacter: true,
      number: true,
      minAmount: true,
    },
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (auth.renewed) {
      props.history.push({
        pathname: '/',
        search: `?email=${encodeURIComponent(formikBag.values.username)}`,
      });
      toast.fire({ title: t('success'), icon: 'success' });
    }
  }, [auth.renewed]);

  useEffect(() => {
    if (auth?.error) {
      const formattedErrors = apolloErrorHandler(auth?.error);
      if (formattedErrors && formattedErrors.length) {
        toast.fire({ title: formattedErrors.join('\n'), icon: 'error' });
      }
    }
  }, [auth.error]);

  return Component({
    ...props,
    t,
    i18n,
    formikBag,
    mfaCodeRequired,
  });
}
